import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CompanyAddonTier, getCompanyAddon } from '@services/companyAddons';
import { getSelectedFacilityFromSession } from '@utils/facilityUtils';

export default function TierPicker({ addonId }: { addonId: string }) {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState<CompanyAddonTier[]>([]);
  const [selected, setSelected] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    const sessionFacility = getSelectedFacilityFromSession<{ id?: number; facility_id?: number }>();
    setLoading(true);
    setError('');
    getCompanyAddon(addonId, sessionFacility?.id ?? sessionFacility?.facility_id)
      .then((addon) => setTiers(addon?.tiers || []))
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Unable to load tiers'),
      )
      .finally(() => setLoading(false));
  }, [addonId]);

  if (loading) return <CircularProgress aria-label="Loading tiers" size={28} />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <Typography variant="h6">Pick a tier</Typography>
      <Typography variant="body2" color="text.secondary">Step 1 of 3 · Pick</Typography>
      {!tiers.length ? (
        <Typography color="text.secondary">No purchasable tiers available for this add-on.</Typography>
      ) : null}
      {tiers.map((tier) => {
        const value = String(tier.id);
        return (
          <Button
            key={value}
            variant={selected === value ? 'contained' : 'outlined'}
            onClick={() => setSelected(value)}
            sx={{ justifyContent: 'space-between', textTransform: 'none' }}
          >
            <span>{tier.name || `Tier ${value}`}</span>
            <span>${tier.price_annual ?? 0}/yr</span>
          </Button>
        );
      })}
      <Button
        variant="contained"
        disabled={!selected}
        onClick={() =>
          navigate(
            `/dashboard/billing?tab=addons&addon=${encodeURIComponent(addonId)}&step=2&tier=${selected}`,
          )
        }
      >
        Continue
      </Button>
    </Box>
  );
}
